import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { releaseStrandedPointerLock } from '@/lib/pointerLock';

// Long enough for a closing dialog's exit animation and Radix's own cleanup to
// finish, so the guard only ever sees a lock nothing is holding.
const SETTLE_MS = 400;

/**
 * App-wide safety net for a page that went dead: whenever the body's lock
 * changes, and on every page change, clear `pointer-events: none` once it is
 * clear that no dialog, menu or select is open. See src/lib/pointerLock.ts.
 */
export function PointerLockGuard() {
  const location = useLocation();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(() => releaseStrandedPointerLock(), SETTLE_MS);
    };
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => releaseStrandedPointerLock(), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  return null;
}
